import { useCallback, useEffect, useState } from "react";
import { Loader2, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSpace } from "@/context/SpaceContext";
import { translateError } from "@/i18n/errors";
import { ReviewItem } from "@/components/reviews/ReviewItem";
import { ConfirmButton } from "@/components/admin/ConfirmButton";

const PAGE = 20;

// Moderation list: admin sees every review (incl. third-party apps), a developer only reviews on their own apps.
export const ReviewsCard = ({ appId = null, title }) => {
  const { t } = useI18n();
  const { base } = useSpace();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);

  const load = useCallback(() => api.get(`${base}/reviews`, { params: { app_id: appId || undefined, skip: page * PAGE, limit: PAGE } })
    .then(({ data: d }) => setData(d)).catch(() => setData({ items: [], total: 0 })), [base, appId, page]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`${base}/reviews/${id}`);
      toast.success(t("reviews.deleted"));
      load();
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid={appId ? "app-reviews-card" : "reviews-card"}>
      <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white">
        <MessageSquare className="h-5 w-5 text-[var(--gold)]" /> {title || t("reviews.manageTitle")} <span className="text-neutral-500" data-testid="reviews-card-total">({data ? data.total : "…"})</span>
      </h3>
      <p className="mt-1 text-xs text-neutral-500">{t("reviews.manageHint")}</p>
      {!data && <Loader2 className="mt-4 h-5 w-5 animate-spin text-[var(--gold)]" />}
      {data?.total === 0 && <p className="mt-4 text-sm text-neutral-500" data-testid="reviews-card-empty">{t("reviews.empty")}</p>}
      <ul className="mt-4 max-h-[32rem] space-y-3 overflow-y-auto" data-testid="reviews-card-list">
        {data?.items.map((r) => (
          <ReviewItem key={r.id} review={appId ? { ...r, app_name: null, developer: null } : r} actions={
            <ConfirmButton title={t("reviews.deleteTitle")} description={t("reviews.deleteAnyDesc")} onConfirm={() => remove(r.id)} className="btn-red btn-sm" testId={`review-admin-delete-${r.id}`}>
              <Trash2 className="h-4 w-4" /> {t("reviews.delete")}
            </ConfirmButton>
          } />
        ))}
      </ul>
      {pages > 1 && (
        <div className="mt-3 flex items-center justify-between text-xs text-neutral-400">
          <button type="button" className="btn-red btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="reviews-card-prev">{t("apps.prev")}</button>
          <span>{t("apps.page", { p: page + 1, n: pages })}</span>
          <button type="button" className="btn-red btn-sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} data-testid="reviews-card-next">{t("apps.next")}</button>
        </div>
      )}
    </section>
  );
};
